import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Moon, Sun } from 'lucide-react';

export type Theme = 'light' | 'dark';
/** 使用者的選擇；system 依手機／電腦的深色模式設定自動切換 */
export type ThemePreference = Theme | 'system';

const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';

export function normalizeTheme(value: string | null | undefined): Theme {
  return value === 'dark' ? 'dark' : 'light';
}

/** 沒有存過偏好時（新使用者）預設跟隨系統 */
export function normalizeThemePreference(value: string | null | undefined): ThemePreference {
  if (value === 'light' || value === 'dark' || value === 'system') return value;
  return 'system';
}

const readSystemDark = (): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(SYSTEM_DARK_QUERY).matches
    : false;

interface ThemeContextType {
  /** 實際套用的主題（system 已換算成 light／dark） */
  theme: Theme;
  preference: ThemePreference;
  setTheme: (preference: ThemePreference) => void;
  isDark: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preference, setPreference] = useState<ThemePreference>(() =>
    normalizeThemePreference(typeof localStorage !== 'undefined' ? localStorage.getItem('theme') : null)
  );
  const [systemDark, setSystemDark] = useState(readSystemDark);

  const setTheme = useCallback((next: ThemePreference) => {
    setPreference(normalizeThemePreference(next));
  }, []);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(SYSTEM_DARK_QUERY);
    const onChange = () => setSystemDark(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const theme: Theme = preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;
  const isDark = theme === 'dark';

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      document.documentElement.style.colorScheme = 'dark';
    } else {
      document.documentElement.classList.remove('dark');
      document.documentElement.style.colorScheme = 'light';
    }
  }, [isDark]);

  useEffect(() => {
    localStorage.setItem('theme', preference);
  }, [preference]);

  const toggleTheme = () => {
    setTheme(isDark ? 'light' : 'dark');
  };

  const contextValue: ThemeContextType = {
    theme,
    preference,
    setTheme,
    isDark,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeToggle: React.FC<{ className?: string; showIcon?: boolean }> = ({
  className = '',
  showIcon = false,
}) => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={isDark ? '切換至淺色模式' : '切換至深色模式'}
      className={`
        h-10 w-10 rounded-lg border-2 shadow-md transition-all duration-200
        ${showIcon ? 'flex items-center justify-center' : ''}
        ${isDark
          ? 'border-gray-600 bg-gray-900 hover:border-gray-500'
          : 'border-gray-300 bg-white hover:border-gray-400'
        }
        ${className}
      `}
    >
      {showIcon &&
        (isDark ? (
          <Sun className="h-[1.125rem] w-[1.125rem] text-amber-300" />
        ) : (
          <Moon className="h-[1.125rem] w-[1.125rem] text-[#1d3557]" />
        ))}
    </button>
  );
};
